import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Menu } from 'primereact/menu';
import { CHAT_ACTIONS_MENU_ITEMS, CHAT_TYPES } from './chatConstants';
import { getChatInfo, getAvatarLabel, getChatDisplayName } from './chatUtils';
import CSConfigUtil from '../../util/CSConfigUtil';
import { fnGetCurrentDate, fnGetCurrentTime } from '../../util/CSDateTimeUtil';
import CSFormUtil from '../../util/CSFormUtil';
import CSCOBackdrop from '../CSCOBackdrop';
import CSServiceUtil from '../../util/CSServiceUtil';
import CSRequestUtil from '../../util/CSRequestUtil';

const gFormUtil              = CSFormUtil.cfGetInstance();
const gServiceUtil           = CSServiceUtil.cfGetInstance();
const gMessagePollIntervalMs = 5000;

const ChatMessagePanel = ({ pChat, onBack, pSaveAction, pReloadAction }) => {
    const [mMessages, setMessages]     = useState([]);
    const [mNewMessage, setNewMessage] = useState('');
    const mScrollPanelRef              = useRef(null);
    const mMenuRef                     = useRef(null);
    const [mShowBackdrop, setBackdrop] = useState(false);

    useEffect(() => {
        if (!pChat) {
            return;
        }

        fnGetMessages();

        const lTimerId = setInterval(() => {
            fnGetMessages();
        }, gMessagePollIntervalMs);

        return () => {
            clearInterval(lTimerId);
        };
    }, [pChat, pReloadAction]);

    const fnScrollToBottom = () => {
        setTimeout(() => {
            if (mScrollPanelRef.current) {
                const lScrollContainer = mScrollPanelRef.current.getContent();
                lScrollContainer.scrollTop = lScrollContainer.scrollHeight;
            }
        }, 100);
    };

    const fnGetMessages = () => {
        if (pReloadAction) {
            const lServiceCode = pReloadAction.on_click_service_code_;
            if (lServiceCode) {
                const lServiceObject = gServiceUtil.cfGetServiceObject(lServiceCode);
                if (lServiceObject) {
                    const lCriteria           = {};
                    lCriteria.to_user_id_     = pChat.user_id_;
                    const lRequestUtil        = new CSRequestUtil('GET', lServiceCode, lServiceObject.type_, null, lServiceObject);
                    lRequestUtil.cfSetRequestCriteria(lCriteria);
                    lRequestUtil.cfProcessRequest((lResponse) => {
                        if (lResponse) {
                            setMessages(lResponse.collection_ || []);
                        }
                    });
                }
            }
        }
    };

    const fnSendMessage = () => {
        if (!mNewMessage || mNewMessage.trim() === '') {
            return;
        }

        const lNewMsg = {
            from_user_id_ : CSConfigUtil.mUserId,
            to_user_id_   : pChat.user_id_,
            date_         : fnGetCurrentDate(),
            time_         : fnGetCurrentTime(),
            message_      : mNewMessage,
            status_       : 'UNREAD'
        };

        if (pSaveAction) {
            setBackdrop(true);
            gFormUtil.cfProcessSave(pSaveAction, lNewMsg, (lResponse, lResponseData, lReqData) => {
                    setBackdrop(false);
                }
            );
        }

        setMessages((lPrev) => [...lPrev, lNewMsg]);
        setNewMessage('');

        setTimeout(fnScrollToBottom, 50);
    };

    const fnHandleKeyDown = (pEvent) => {
        if (pEvent.key === 'Enter' && !pEvent.shiftKey) {
            pEvent.preventDefault();
            fnSendMessage();
        }
    };

    if (!pChat) {
        return (
            <div className="flex flex-column flex-grow-1 bg-primary align-items-center justify-content-center p-4 no-chat-selected">
                <div className="text-center">
                    <i className="pi pi-comments text-8xl mb-4" style={{ color: '#4A5568' }}></i>
                    <h3 className="text-2xl font-semibold text-300 mb-2">Welcome to School Chat</h3>
                    <p className="text-500 mb-4">Select a chat to start messaging</p>
                </div>
            </div>
        );
    }

    const lDisplayName = getChatDisplayName(pChat);
    const lSubTitle    = getChatInfo(pChat);
    const lMessageBlocks = [];
    let lLastDateKey     = null;

    mMessages.forEach((lMessage, lIndex) => {
        const lDateKey = lMessage.date_ || 'NO_DATE';

        if (lDateKey !== lLastDateKey) {
            lMessageBlocks.push({
                type : 'date',
                key  : `date-${lDateKey}-${lIndex}`,
                label: lDateKey
            });
            lLastDateKey = lDateKey;
        }

        lMessageBlocks.push({
            type   : 'message',
            key    : `msg-${lMessage.id_ ?? lIndex}`,
            message: lMessage
        });
    });

    return (
        <div className="flex flex-column h-full bg-primary chat-panel-container">
            <CSCOBackdrop pShow={mShowBackdrop} />

            <div className="flex align-items-center p-2 border-bottom-1 shadow-1 chat-header bg-primary-text">
                {onBack && (
                    <Button
                        icon      = "pi pi-arrow-left"
                        className = "p-button-text p-button-plain mr-2 md:hidden"
                        onClick   = {onBack}
                        tooltip   = "Back to chats"
                    />
                )}

                <Avatar
                    image     = {pChat.avatar}
                    label     = {getAvatarLabel(pChat)}
                    shape     = {pChat.type === CHAT_TYPES.GROUP ? 'square' : 'circle'}
                    className = "mr-3 border-2 surface-600"
                    style     = {{
                        width    : '24px',
                        height   : '24px',
                        fontSize : '0.7rem'
                    }}
                />

                <div className="flex flex-column flex-grow-1">
                    <span
                        className="font-bold text-base text-sm"
                        style={{ color: '#F7FAFC' }}
                    >
                        {lDisplayName}
                    </span>

                    <span 
                        className="text-xs" 
                        style={{ color: '#E2E8F0' }}
                    > 
                        {lSubTitle}
                    </span>
                </div>

                <div className="flex gap-2">
                    <Button
                        icon      = "pi pi-search"
                        className = "p-button-rounded p-button-text p-button-plain"
                        tooltip   = "Search"
                    />

                    <Button
                        icon      = "pi pi-ellipsis-v"
                        className = "p-button-rounded p-button-text p-button-plain"
                        onClick   = {(pEvent) => mMenuRef.current.toggle(pEvent)}
                        tooltip   = "More options"
                    />

                    <Menu model={CHAT_ACTIONS_MENU_ITEMS} popup ref={mMenuRef} />
                </div>
            </div>

            <ScrollPanel
                ref={mScrollPanelRef}
                className="flex-grow-1 p-1 messages-scroll-panel bg-primary-light"
                style={{
                    background: '',
                    minHeight : '320px'
                }}
            >
                <div className="flex flex-column gap-3 messages-container">
                    {lMessageBlocks.map((lBlock) => {
                        if (lBlock.type === 'date') {
                            return (
                                <div
                                    key={lBlock.key}
                                    className="flex justify-content-center my-2"
                                >
                                    <span
                                        className="px-3 py-1 text-xs border-round-xl shadow-1"
                                        style={{
                                            backgroundColor: '#E2E8F0',
                                            color          : '#2D3748'
                                        }}
                                    >
                                        {lBlock.label}
                                    </span>
                                </div>
                            );
                        }

                        const lMessage      = lBlock.message;
                        const lIsCurrentUser = (lMessage.from_user_id_ === CSConfigUtil.mUserId);
                        return (
                            <div
                                key={lBlock.key}
                                className={`flex ${lIsCurrentUser ? 'justify-content-end' : 'justify-content-start'}`}
                            >
                                <div
                                    className={
                                        `border-round-2xl message-bubble flex flex-column ${
                                            lIsCurrentUser
                                                ? 'bg-primary text-800 border-noround-right'
                                                : 'bg-primary text-100 border-noround-left'
                                        }`
                                    }
                                    style={{
                                        maxWidth : '60%',
                                        padding  : '6px 10px',
                                        lineHeight: 1.2
                                    }}
                                >
                                    <div
                                        className="message-text text-xs text-800"
                                        style={{
                                            fontSize    : '0.85rem',
                                            marginBottom: '2px',
                                            whiteSpace  : 'pre-wrap'
                                        }}
                                    >
                                        {lMessage.message_}
                                    </div>

                                    <div
                                        className="flex align-items-center text-800"
                                        style={{
                                            justifyContent: 'flex-end',
                                            gap           : '8px',
                                            fontSize      : '0.70rem',
                                            opacity       : 0.8
                                        }}
                                    >
                                        {lMessage.time_}
                                        {lIsCurrentUser && (
                                            <i
                                                className="pi pi-check"
                                                style={{
                                                    fontSize: '0.65rem',
                                                    color   : lMessage.status_ === 'READ'
                                                        ? '#A0AEC0'
                                                        : '#718096'
                                                }}
                                            ></i>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </ScrollPanel>

            <div className="p-3 border-top-1 bg-primary-light message-input-container">
                <div className="flex align-items-center gap-2">
                    <Button
                        icon      = "pi pi-paperclip"
                        className = "p-button-rounded p-button-text p-button-plain"
                        tooltip   = "Attach file"
                    />

                    <div className="flex-grow-1">
                        <InputText
                            placeholder = {lDisplayName ? `Message ${lDisplayName}` : 'Type a message'}
                            className   = "w-full border-noround message-input"
                            style={{
                                borderRadius    : '20px',
                                backgroundColor : '#FFFFFF',
                                border          : '1px solid #CBD5E0',
                                color           : '#1A202C',
                                padding         : '8px 14px',
                                fontSize        : '0.8rem'
                            }}
                            value      = {mNewMessage}
                            onChange   = {(pEvent) => setNewMessage(pEvent.target.value)}
                            onKeyDown  = {fnHandleKeyDown}
                        />
                    </div>

                    <Button
                        icon        = {mNewMessage ? 'pi pi-send' : 'pi pi-microphone'}
                        className   = "p-button-rounded p-button-primary send-button"
                        onClick     = {fnSendMessage}
                        disabled    = {!mNewMessage.trim()}
                        tooltip     = {mNewMessage ? 'Send message' : 'Voice message'}
                    />
                </div>
            </div>
        </div>
    );
};

export default ChatMessagePanel;